import {
  Box,
  Card,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
} from "utils/constants/constant";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  ICompletedSLPRequest,
  IEnhancedTablePropsForSLPCompleted,
  ISLPCompletedKeys,
  ISLPDashboardListMeta,
  ISLPRequestsPayloadValues,
} from "utils/interfaces/slp";
import { courseListSecondaryIcon, deleteIcon, editIcon } from "assets/images";
import { useCallback, useEffect, useState } from "react";
import {
  CompletedSLPListPayload,
  CompletedSLPListSuccessPayload,
} from "store/slpDashboard/type";
import {
  getPagePayloadValues,
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import moment from "moment";
import CustomPagination from "components/customPagination";
import NoRecords from "components/NoRecords";
import { useHistory, useLocation } from "react-router";
import {
  DeleteCompletedSLPPayload,
  DeleteCompletedSLPSuccessPayload,
} from "store/studentProfileDetails/types";
import { toast } from "react-toastify";
import { AppRoutings } from "utils/enums/app-routings";
import {
  DELETE_COMPLETED_SLP_CONFIRMATION_MSG,
  SLP_DELETED_MSG,
} from "utils/constants/Messages";
import { SLPFormStep } from "utils/enums/slp-form";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";

const EnhancedTableHead = (props: IEnhancedTablePropsForSLPCompleted) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof ISLPCompletedKeys) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

interface ICompletedSLPListProps {
  completedSLPListRequest: (payload: CompletedSLPListPayload) => void;
  deleteCompletedSLPRequest: (payload: DeleteCompletedSLPPayload) => void;
}

const CompletedSLPList: React.FC<ICompletedSLPListProps> = (props) => {
  const location = useLocation<{ studentId: number }>();
  const { studentId } = location.state;
  const history = useHistory();
  const [completedSLPs, setCompletedSLPs] = useState<
    Array<ICompletedSLPRequest>
  >([]);
  const [completedSLPsMeta, setCompletedSLPsMeta] =
    useState<ISLPDashboardListMeta>();
  const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Desc);
  const [orderBy, setOrderBy] =
    useState<keyof ISLPCompletedKeys>("dateCompleted");
  const { completedSLPListRequest, deleteCompletedSLPRequest } = props;
  const [isOpenDocumentDeleteModal, setIsOpenDocumentDeleteModal] =
    useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const getSLPRequests = () => {
    if (completedSLPListRequest && studentId) {
      showLoaderForBatchApiCall();
      const values: ISLPRequestsPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.studentId = studentId;
      values.search = "";

      const payload = {
        values,
        callback: (data: CompletedSLPListSuccessPayload) => {
          hideLoaderForBatchApiCall();
          setCompletedSLPs(data.completedSLPs);
          setCompletedSLPsMeta(data.completedSLPsPagination);
        },
      };
      completedSLPListRequest(payload);
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    getSLPRequests();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [page, pageNo, order, orderBy]);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof ISLPCompletedKeys) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const DeleteCompletedSLP = (id: number) => {
    if (deleteCompletedSLPRequest) {
      showLoaderForBatchApiCall();
      const values = { id };
      const payload = {
        values,
        callback: (response: DeleteCompletedSLPSuccessPayload) => {
          hideLoaderForBatchApiCall();
          if (response.isSuccessStatusCode) {
            toast.success(SLP_DELETED_MSG);
            getSLPRequests();
          }
        },
      };
      deleteCompletedSLPRequest(payload);
    }
  };

  const handleDocumentDeleteCloseModal = () => {
    setIsOpenDocumentDeleteModal(false);
    setDeleteId(null);
  };

  const handleDeleteModal = (slpId: number) => {
    setDeleteId(slpId);
    setIsOpenDocumentDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) DeleteCompletedSLP(deleteId);
    handleDocumentDeleteCloseModal();
  };

  return (
    <Grid item xs={12}>
      <Card>
        <Box className="card-inner-head mb-24">
          <Typography variant="h3" sx={{ fontWeight: 500 }}>
            Completed SLPs
          </Typography>
        </Box>
        <TableContainer>
          <Table className="table-h-md">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    maxWidth: "84px",
                    width: "84px",
                    minWidth: "84px",
                  }}
                >
                  SLP Number
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: "154px", minWidth: "154px" }}
                >
                  Status
                </TableCell>
                <TableCell sx={{ width: "130px", minWidth: "130px" }}>
                  Allocated To
                </TableCell>
                <EnhancedTableHead
                  onRequestSort={(e) => handleRequestSort(e, "dateCompleted")}
                  order={order}
                  orderBy={orderBy}
                  fieldName="Date Completed"
                  keyName="dateCompleted"
                  sxStyle={{ width: "200px", minWidth: "200px" }}
                />
                <TableCell align="center" sx={{ width: "92px" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completedSLPs.map((row) => (
                <TableRow>
                  <TableCell component="th" scope="row" align="center">
                    {row.slpNumber}
                  </TableCell>
                  <TableCell align="center" className="completed status">
                    <div>Completed</div>
                  </TableCell>
                  <TableCell>{row.allocatedToName ?? "-"}</TableCell>
                  <TableCell>
                    {row.dateCompleted
                      ? moment(row.dateCompleted).format(DATE_VIEW_FORMAT)
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <div className="table-actions">
                      <Tooltip arrow title="View">
                        <IconButton
                          onClick={() =>
                            history.push({
                              pathname: AppRoutings.SLPView,
                              state: {
                                slpId: Number(row.id),
                                studentId,
                              },
                            })
                          }
                        >
                          <img src={courseListSecondaryIcon} alt="Edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title="Edit">
                        <IconButton
                          onClick={() =>
                            history.push({
                              pathname: AppRoutings.SLPForm,
                              state: {
                                studentId,
                                slpId: row.id,
                                partNumber: SLPFormStep.HOPES_GOALS_AMBITIONS,
                                from: AppRoutings.StudentProfileDetails,
                                isCompletedSLP: true,
                              },
                            })
                          }
                        >
                          <img src={editIcon} alt="Edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title="Delete SLP">
                        <IconButton onClick={() => handleDeleteModal(row.id)}>
                          <img src={deleteIcon} alt="Delete" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {completedSLPsMeta && !!completedSLPs.length ? (
          <CustomPagination
            handleChange={handleChange}
            page={page}
            pageNo={pageNo}
            onPageChange={onPageChange}
            listMeta={completedSLPsMeta}
          />
        ) : (
          <NoRecords />
        )}
      </Card>
      <DeleteConfirmationModal
        isOpenDeleteConfirmationModal={isOpenDocumentDeleteModal}
        handleDeleteConfirmationModalClose={handleDocumentDeleteCloseModal}
        deleteConfirmationMessage={DELETE_COMPLETED_SLP_CONFIRMATION_MSG}
        handleYesClick={handleDeleteConfirm}
      />
    </Grid>
  );
};

export default CompletedSLPList;
